import * as React from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import {
  ChevronDownIcon,
  ExternalLinkIcon,
  PanelLeftCloseIcon,
  PanelLeftOpenIcon
} from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuTrigger
} from '@/components/ui/dropdown-menu';
import { useSession } from '@/components/auth/user-provider';
import { useListPhoneApprovals } from '@cdf/api-client-react';
import {
  adminNavigation,
  filterNavigationForRole,
  isAdminAreaPath,
  mainNavigation,
  type NavItem,
  type NavSection
} from '@/lib/navigation';
import { cn } from '@/lib/utils';

const GROUPS_STORAGE_KEY = 'cdf-sidebar-open-groups';

/** Read the persisted open/closed state of expandable sidebar groups. */
function readOpenGroups(): Record<string, boolean> {
  try {
    const raw = localStorage.getItem(GROUPS_STORAGE_KEY);
    const parsed: unknown = raw ? JSON.parse(raw) : {};
    return parsed && typeof parsed === 'object' ? (parsed as Record<string, boolean>) : {};
  } catch {
    return {};
  }
}

function persistOpenGroup(title: string, open: boolean) {
  const groups = readOpenGroups();
  groups[title] = open;
  localStorage.setItem(GROUPS_STORAGE_KEY, JSON.stringify(groups));
}

const baseLinkClasses =
  'focus-visible:ring-sidebar-ring flex items-center gap-2 rounded-lg px-3 py-2 text-sm font-medium transition-all duration-200 focus-visible:ring-2 focus-visible:outline-none motion-reduce:transition-none';

function linkClasses({ isActive }: { isActive: boolean }, collapsed = false) {
  return cn(
    baseLinkClasses,
    isActive
      ? 'bg-sidebar-primary text-sidebar-primary-foreground shadow-sm shadow-primary/20'
      : 'text-sidebar-foreground/65 hover:bg-sidebar-accent hover:text-sidebar-accent-foreground',
    collapsed && 'justify-center px-0'
  );
}

function SidebarLink({ item, collapsed }: { item: NavItem; collapsed: boolean }) {
  const location = useLocation();
  const isAdminEntry = item.to === '/admin';
  const adminAreaActive = isAdminEntry && isAdminAreaPath(location.pathname);

  if (!item.to && !item.href) return null;

  const resolveClass = ({ isActive }: { isActive: boolean }) =>
    linkClasses({ isActive: isActive || adminAreaActive }, collapsed);

  const link = item.to ? (
    <NavLink
      to={item.to}
      end={item.to === '/' || isAdminEntry}
      aria-label={collapsed ? item.title : undefined}
      className={resolveClass}
    >
      <item.icon aria-hidden className="size-4 shrink-0" />
      {!collapsed && <span className="truncate">{item.title}</span>}
      {isAdminEntry && !collapsed ? <PendingApprovalsBadge /> : null}
    </NavLink>
  ) : (
    <a
      href={item.href}
      target={item.external ? '_blank' : undefined}
      rel={item.external ? 'noreferrer' : undefined}
      aria-label={collapsed ? item.title : undefined}
      className={linkClasses({ isActive: false }, collapsed)}
    >
      <item.icon aria-hidden className="size-4 shrink-0" />
      {!collapsed && <span className="truncate">{item.title}</span>}
      {!collapsed && item.external && (
        <ExternalLinkIcon aria-hidden className="text-muted-foreground ml-auto size-3 shrink-0" />
      )}
      {item.external && <span className="sr-only">(opens in new tab)</span>}
    </a>
  );

  if (!collapsed) return link;

  return (
    <Tooltip>
      <TooltipTrigger asChild>{link}</TooltipTrigger>
      <TooltipContent side="right">{item.title}</TooltipContent>
    </Tooltip>
  );
}

function PendingApprovalsBadge() {
  const { session } = useSession();
  const isAdmin = session.status === 'signedIn' && session.user.role === 'admin';
  const { data } = useListPhoneApprovals({ status: 'pending' });
  const count = data?.total ?? 0;
  if (!isAdmin || count === 0) return null;
  return (
    <span className="ml-auto rounded-full bg-destructive/15 px-1.5 py-0.5 text-[10px] font-bold tabular-nums text-destructive">
      {count}
    </span>
  );
}

function SidebarGroup({ item, collapsed }: { item: NavItem; collapsed: boolean }) {
  const location = useLocation();
  const children = item.children ?? [];
  const childActive = children.some(
    (child) =>
      location.pathname === child.to ||
      (child.to !== '/' &&
        child.to !== '/admin' &&
        location.pathname.startsWith(`${child.to}/`))
  );
  const parentActive = Boolean(item.to && location.pathname === item.to);
  const groupActive = childActive || parentActive;
  const [open, setOpen] = React.useState(
    () => readOpenGroups()[item.title] ?? childActive
  );

  React.useEffect(() => {
    if (childActive) setOpen(true);
  }, [childActive]);

  const toggleOpen = () => {
    setOpen((value) => {
      persistOpenGroup(item.title, !value);
      return !value;
    });
  };

  // Collapsed rail: expose sub items via a flyout menu instead of inline expansion.
  if (collapsed) {
    return (
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            type="button"
            aria-label={item.title}
            className={cn(
              'focus-visible:ring-sidebar-ring flex w-full items-center gap-2 rounded-lg px-3 py-2 text-sm font-medium transition-all duration-200 focus-visible:ring-2 focus-visible:outline-none motion-reduce:transition-none',
              childActive
                ? 'bg-sidebar-primary text-sidebar-primary-foreground shadow-sm shadow-primary/20'
                : 'text-sidebar-foreground/65 hover:bg-sidebar-accent hover:text-sidebar-accent-foreground'
            )}
          >
            <item.icon aria-hidden className="size-4 shrink-0" />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent side="right" align="start">
          <DropdownMenuLabel>{item.title}</DropdownMenuLabel>
          {children.map((child) => (
            <DropdownMenuItem key={child.to} asChild>
              <NavLink to={child.to}>{child.title}</NavLink>
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>
    );
  }

  return (
    <div>
      <button
        type="button"
        onClick={toggleOpen}
        aria-expanded={open}
        className={cn(
          'focus-visible:ring-sidebar-ring flex w-full items-center gap-2 rounded-lg px-3 py-2 text-sm font-medium transition-all duration-200 focus-visible:ring-2 focus-visible:outline-none motion-reduce:transition-none',
          groupActive
            ? 'text-sidebar-foreground font-semibold'
            : 'text-sidebar-foreground/65 hover:bg-sidebar-accent hover:text-sidebar-accent-foreground'
        )}
      >
        <item.icon aria-hidden className="size-4 shrink-0" />
        <span className="flex-1 truncate text-left">{item.title}</span>
        <ChevronDownIcon
          aria-hidden
          className={cn(
            'size-3.5 transition-transform motion-reduce:transition-none',
            open ? 'rotate-180' : 'rotate-0'
          )}
        />
      </button>
      {open && (
        <div className="border-sidebar-border/80 mt-1 ml-5 flex flex-col gap-0.5 border-l pl-3">
          {children.map((child) => (
            <NavLink
              key={child.to}
              to={child.to}
              end
              className={({ isActive }) =>
                cn(
                  'focus-visible:ring-sidebar-ring flex items-center rounded-lg px-2.5 py-1.5 text-sm font-medium transition-all duration-200 focus-visible:ring-2 focus-visible:outline-none motion-reduce:transition-none',
                  isActive
                    ? 'bg-sidebar-primary text-sidebar-primary-foreground shadow-sm shadow-primary/20'
                    : 'text-sidebar-foreground/65 hover:bg-sidebar-accent hover:text-sidebar-accent-foreground'
                )
              }
            >
              <span>{child.title}</span>
              {child.to === '/approved-numbers' && <PendingApprovalsBadge />}
            </NavLink>
          ))}
        </div>
      )}
    </div>
  );
}

function SidebarSection({
  section,
  collapsed,
  hideHeading = false
}: {
  section: NavSection;
  collapsed: boolean;
  hideHeading?: boolean;
}) {
  return (
    <div className="flex flex-col gap-0.5">
      {!collapsed && !hideHeading && (
        <p className="text-sidebar-foreground/45 px-3 pt-3 pb-1 text-[10px] font-bold tracking-[0.16em] uppercase">
          {section.heading}
        </p>
      )}
      {section.items.map((item) =>
        item.children ? (
          <SidebarGroup key={item.title} item={item} collapsed={collapsed} />
        ) : (
          <SidebarLink key={item.title} item={item} collapsed={collapsed} />
        )
      )}
    </div>
  );
}

/** Inner sidebar panel — shared by desktop inline and mobile overlay. */
function SidebarPanel({
  collapsed,
  onToggle,
  onClose
}: {
  collapsed: boolean;
  onToggle: () => void;
  onClose?: () => void;
}) {
  const { session } = useSession();
  const role = session.status === 'signedIn' ? session.user.role : undefined;
  const visibleMain = filterNavigationForRole(mainNavigation, role);
  const visibleAdmin = filterNavigationForRole([adminNavigation], role);
  const adminSection = visibleAdmin[0];

  return (
    <>
      {/* Collapse / close toggle */}
      <div
        className={cn(
          'border-sidebar-border/70 flex items-center border-b px-2 py-2',
          collapsed ? 'justify-center px-0' : 'justify-end'
        )}
      >
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              onClick={onClose ?? onToggle}
              aria-label={onClose ? 'Close navigation' : collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
              className="shrink-0"
            >
              {onClose ? (
                <PanelLeftCloseIcon className="size-4" />
              ) : collapsed ? (
                <PanelLeftOpenIcon className="size-4" />
              ) : (
                <PanelLeftCloseIcon className="size-4" />
              )}
            </Button>
          </TooltipTrigger>
          <TooltipContent side="right">
            {onClose ? 'Close' : collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          </TooltipContent>
        </Tooltip>
      </div>

      {/* User navigation — filtered by role */}
      <nav aria-label="Main" className="flex flex-1 flex-col gap-3 overflow-y-auto p-3">
        {visibleMain
          .filter((section) => section.heading !== 'Resources')
          .map((section) => (
            <SidebarSection key={section.heading} section={section} collapsed={collapsed} />
          ))}

        {/* Bottom-aligned area: Resources, then admin settings */}
        <div className="mt-auto flex flex-col gap-2">
          {visibleMain
            .filter((section) => section.heading === 'Resources')
            .map((section) => (
              <SidebarSection key={section.heading} section={section} collapsed={collapsed} />
            ))}

          {/* Admin area — separated from user menus, role-gated */}
          {adminSection ? (
            <div className="border-sidebar-border border-t pt-3">
              <SidebarSection section={adminSection} collapsed={collapsed} hideHeading />
            </div>
          ) : null}
        </div>
      </nav>
    </>
  );
}

export function Sidebar({
  collapsed,
  onToggle,
  mobileOpen,
  onMobileClose
}: {
  collapsed: boolean;
  onToggle: () => void;
  mobileOpen: boolean;
  onMobileClose: () => void;
}) {
  // Close mobile sidebar on route change
  const location = useLocation();
  const prevPathRef = React.useRef(location.pathname);
  React.useEffect(() => {
    if (location.pathname !== prevPathRef.current) {
      prevPathRef.current = location.pathname;
      onMobileClose();
    }
  }, [location.pathname, onMobileClose]);

  return (
    <>
      {/* ── Desktop inline panel ── */}
      <aside
        data-collapsed={collapsed}
        className={cn(
          'bg-sidebar text-sidebar-foreground border-sidebar-border hidden h-full shrink-0 flex-col border-r shadow-sm transition-[width] duration-200 motion-reduce:transition-none md:flex',
          collapsed ? 'w-16' : 'w-64'
        )}
      >
        <SidebarPanel collapsed={collapsed} onToggle={onToggle} />
      </aside>

      {/* ── Mobile slide-in overlay ── */}
      {/* Backdrop */}
      <div
        aria-hidden="true"
        onClick={onMobileClose}
        className={cn(
          'fixed inset-0 z-40 bg-black/40 transition-opacity duration-200 md:hidden motion-reduce:transition-none',
          mobileOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'
        )}
      />
      {/* Panel */}
      <aside
        aria-label="Navigation"
        className={cn(
          'bg-sidebar text-sidebar-foreground border-sidebar-border fixed inset-y-0 left-0 z-50 flex w-72 flex-col border-r shadow-2xl shadow-primary/20 transition-transform duration-200 motion-reduce:transition-none md:hidden',
          mobileOpen ? 'translate-x-0' : '-translate-x-full'
        )}
      >
        <SidebarPanel collapsed={false} onToggle={onToggle} onClose={onMobileClose} />
      </aside>
    </>
  );
}
